import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { PageHeader, Stat, Stats, DataTable, FilterBar } from '../../components/office';
import WeekPicker from '../../components/people/WeekPicker';
import { wastageApi, stockApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { useAuthoringSite } from '../../contexts/SiteContext';
import { mondayOf } from '../../lib/weeks';
import { fromSmall, qty, smallUnit } from '../../lib/units';

// Waste (Back office sweep, Oct 2026). What went in the bin, a week at a time, and what it
// cost. Logging it says the unit beside the quantity ("1 kg"), takes grams for a kg item,
// and shows the value lost as you type, so nobody logs 250 kg of butter meaning 250 g.

const REASONS = [
  { value: 'spoilage', label: 'Gone off', tone: 'stop' },
  { value: 'spillage', label: 'Spilt', tone: 'info' },
  { value: 'breakage', label: 'Broken', tone: 'warn' },
  { value: 'overpouring', label: 'Overpoured', tone: 'info' },
  { value: 'other', label: 'Other', tone: null },
];
const reasonOf = (r) => REASONS.find((x) => x.value === r) || { label: r, tone: null };
const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const when = (iso) => new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const EMPTY = { stock_item_id: '', quantity: '', small: false, reason: 'spoilage', notes: '' };

const WasteEventsPage = () => {
  const authoring = useAuthoringSite();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [events, setEvents] = useState(null);
  const [stock, setStock] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    const end = new Date(weekStart); end.setDate(end.getDate() + 7);
    return wastageApi.getAll(0, undefined, { start_date: weekStart.toISOString(), end_date: end.toISOString() })
      .then((r) => setEvents(r.data || []))
      .catch(() => { setEvents([]); toast.error("Couldn't load waste"); });
  };
  useEffect(() => { setEvents(null); load(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { stockApi.getAll().then((r) => setStock(r.data || [])).catch(() => {}); }, []);

  const all = useMemo(() => events || [], [events]);
  const total = all.reduce((t, e) => t + (e.cost ?? 0), 0);
  const byReason = useMemo(() => {
    const m = {};
    for (const e of all) { (m[e.reason] ||= { reason: e.reason, cost: 0, count: 0 }); m[e.reason].cost += e.cost ?? 0; m[e.reason].count += 1; }
    return Object.values(m).sort((a, b) => b.cost - a.cost);
  }, [all]);
  const byItem = useMemo(() => {
    const m = {};
    for (const e of all) { m[e.stock_item_name] = (m[e.stock_item_name] || 0) + (e.cost ?? 0); }
    return Object.entries(m).sort((a, b) => b[1] - a[1])[0];
  }, [all]);

  const item = stock.find((s) => s.id === form.stock_item_id);
  const small = item ? smallUnit(item.unit) : null;
  const amount = form.quantity === '' ? 0 : (form.small && small ? fromSmall(form.quantity) : Number(form.quantity));
  const lost = item ? amount * (Number(item.cost_per_unit) || 0) : 0;
  const tooMuch = item && amount > Number(item.quantity || 0);

  const save = async () => {
    if (!item) { toast.error('Pick what went in the bin'); return; }
    if (!(amount > 0)) { toast.error('How much?'); return; }
    setSaving(true);
    try {
      await wastageApi.log({ stock_item_id: item.id, quantity: Math.round(amount * 1000) / 1000, reason: form.reason, notes: form.notes.trim() }, authoring.site);
      toast.success('Waste logged successfully');
      setOpen(false);
      setForm(EMPTY);
      setStock((cur) => cur.map((s) => (s.id === item.id ? { ...s, quantity: Math.max(0, s.quantity - amount) } : s)));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't log it"); } finally { setSaving(false); }
  };

  const columns = [
    { key: 'created_at', label: 'When', sortable: true, muted: true, className: 'whitespace-nowrap', render: (e) => when(e.created_at) },
    { key: 'stock_item_name', label: 'Item', sortable: true, render: (e) => (
      <div><div className="font-medium text-ink">{e.stock_item_name}</div>{e.notes && <div className="text-[12.5px] text-ink-soft">{e.notes}</div>}</div>
    ) },
    { key: 'quantity', label: 'How much', mono: true, render: (e) => qty(e.quantity, e.unit) },
    { key: 'reason', label: 'Why', render: (e) => { const r = reasonOf(e.reason); return <Signal tone={r.tone || 'info'} dot={false}>{r.label}</Signal>; } },
    { key: 'created_by', label: 'By', muted: true },
    { key: 'cost', label: 'Cost', align: 'right', mono: true, sortable: true, render: (e) => money(e.cost ?? e.estimated_cost) },
  ];

  return (
    <div className="space-y-5" data-testid="waste-events-page">
      <PageHeader
        title="Waste"
        purpose="What went in the bin, and what it cost."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={() => { setForm(EMPTY); setOpen(true); }} disabled={!authoring.ready} title={authoring.reason || undefined} data-testid="log-waste-btn"><Plus size={16} className="mr-2" />Log waste</Button>}
      />

      <FilterBar><WeekPicker weekStart={weekStart} onChange={setWeekStart} testId="waste-week" /></FilterBar>

      <Stats>
        <Stat label="Cost this week" value={events ? money(total) : null} testId="waste-total" />
        <Stat label="Times logged" value={events ? all.length : null} />
        <Stat label="Biggest reason" value={events ? (byReason[0] ? reasonOf(byReason[0].reason).label : '—') : null} hint={byReason[0] ? money(byReason[0].cost) : undefined} />
        <Stat label="Biggest item" value={events ? (byItem ? byItem[0] : '—') : null} hint={byItem ? money(byItem[1]) : undefined} />
      </Stats>

      {byReason.length > 1 && (
        <div className="rounded-lg border border-hairline bg-white px-5 py-4" aria-label="By reason">
          <p className="mb-3 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">By reason</p>
          <div className="space-y-2">
            {byReason.map((r) => (
              <div key={r.reason} className="grid grid-cols-[110px_minmax(0,1fr)_80px] items-center gap-3 text-[13px]">
                <span className="text-ink">{reasonOf(r.reason).label} <span className="text-ink-faint">· {r.count}</span></span>
                <div className="h-1.5 rounded-full bg-hairline"><div className="h-1.5 rounded-full bg-signal-stop-line" style={{ width: `${total ? (r.cost / total) * 100 : 0}%` }} /></div>
                <span className="text-right font-mono tabular-nums text-ink">{money(r.cost)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <DataTable
        columns={columns} rows={all} rowKey="id" loading={events === null}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        empty={{ word: 'Waste', title: 'Nothing logged this week', children: 'When something goes in the bin, log it here, and stock comes down with it.' }}
        testId="waste-table"
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="form">
          <DialogHeader>
            <DialogTitle>Log waste</DialogTitle>
            <DialogDescription>It comes off stock, at what you last paid for it.</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">What</Label>
              <Select value={form.stock_item_id} onValueChange={(v) => setForm({ ...form, stock_item_id: v, small: false })}>
                <SelectTrigger data-testid="waste-stock-item-select"><SelectValue placeholder="Pick an item" /></SelectTrigger>
                <SelectContent>{stock.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {qty(s.quantity, s.unit)} in stock</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">How much</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input type="number" min="0" step="any" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="w-28 font-mono tabular-nums" placeholder="0" data-testid="waste-quantity" aria-label="How much" />
                {item && (small
                  ? <Segmented value={form.small ? 'small' : 'big'} onChange={(v) => setForm({ ...form, small: v === 'small' })} data-testid="waste-unit"
                      options={[{ value: 'big', label: item.unit }, { value: 'small', label: small }]} />
                  : <span className="text-[14px] text-ink-soft">{item.unit === 'each' ? '' : item.unit}</span>)}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">Why</Label>
              <div><Segmented value={form.reason} onChange={(reason) => setForm({ ...form, reason })} data-testid="waste-reason" options={REASONS.map((r) => ({ value: r.value, label: r.label }))} /></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">Note <span className="text-ink-faint">(optional)</span></Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={300} placeholder="e.g. Fridge 2 was warm overnight" />
            </div>
            {item && amount > 0 && (
              <p className="rounded-md bg-surface-2 px-3 py-2 text-[13.5px] text-ink" data-testid="waste-lost">
                That's <b className="font-mono">{money(lost)}</b> of {item.name}: {qty(item.quantity, item.unit)} in stock goes to {qty(Math.max(0, Math.round((item.quantity - amount) * 1000) / 1000), item.unit)}.
                {tooMuch && <span className="text-signal-stop"> That's more than there is.</span>}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="waste-save">{saving ? 'Logging…' : 'Log waste'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WasteEventsPage;
